/**
 * Kommunikation als Chat-Verlauf je Kontakt (Anforderung 01.10.2026).
 *
 * Bisher war die E-Mail-Ansicht ein persönliches Postfach wie Outlook
 * (Posteingang / Gesendet). Über KARS kommunizieren aber mehrere Mitarbeiter
 * eines Unternehmens: Jeder soll den gesamten Verlauf mit einem Kontakt
 * sehen, auch was Kollegen gesendet haben. Dieses Modul bündelt deshalb die
 * Unterhaltungen nach der Gegenseite (z. B. "Autohaus Müller") und baut den
 * Verlauf als Chat auf: eigene Organisation rechts, Gegenseite links.
 *
 * Reine Funktionen ohne Vue/DOM – Grundlage für MessageCenter und Tests.
 */
import { participantDisplayName } from '../participantDisplay'

// ── Typen ──────────────────────────────────────────────────────────────

export interface Viewer {
  idUser: number
  idOrganization: number
}

export interface ChatParticipant {
  id?: number
  idConversation?: number
  idUser?: number | null
  userName?: string | null
  initials?: string | null
  image?: string | null
  userEmail?: string | null
  firstName?: string | null
  lastName?: string | null
  idOrganization?: number | null
  organizationName?: string | null
  externalEmail?: string | null
  externalName?: string | null
  role?: string | null
}

export interface ChatAttachment {
  id: number
  idMessage: number
  filename: string
  mimeType?: string
  fileSize?: number
}

export interface ChatMessage {
  id: number
  idConversation: number
  idUser?: number | null
  content: string
  createdAt: string
  userName?: string | null
  initials?: string | null
  image?: string | null
  senderEmail?: string | null
  senderName?: string | null
  sourceKind?: string | null
  senderIdOrganization?: number | null
  senderOrganizationName?: string | null
  attachments?: ChatAttachment[] | null
  delivery?: unknown
  receipts?: unknown[]
  /** Tatsächliche E-Mail-Empfänger (aus den Zustellungen, ohne Archivkopie). */
  recipients?: Array<{ email: string, name?: string | null, role: string }> | null
  /** Antwort nur an diese Beteiligten (Teilnehmer-IDs); fehlt = an alle. */
  recipientParticipantIds?: number[] | null
  /** Dokument-ID des in der E-Akte abgelegten PDFs (Kundenfeedback 02.10.2026, IV); fehlt = nicht abgelegt. */
  idFiledDocument?: number | null
  /** Automatische Abwesenheitsantwort (Kundenfeedback 18.10.2026, V). */
  isAutoReply?: boolean
}

export interface ChatConversation {
  id: number
  idCase?: number | null
  caseNumber?: string | null
  caseDescription?: string | null
  subject?: string | null
  isUrgent?: boolean
  participants?: ChatParticipant[] | null
  lastMessage?: ChatMessage | null
  lastIncomingMessage?: ChatMessage | null
  lastOutgoingMessage?: ChatMessage | null
  unreadCount?: number
  createdAt?: string
  updatedAt?: string
  /** Offenes Zustellproblem in dieser Unterhaltung (Anforderung 2.3). */
  deliveryProblem?: boolean
  /** Liegt für den angemeldeten Nutzer im Papierkorb (nur Ansicht, nie gelöscht). */
  trashed?: boolean
  /** „Nicht zuordnen": soll bewusst keiner Akte zugeordnet werden (organisationsweit). */
  noCase?: boolean
}

export interface ContactLike {
  id: number
  companyName?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  email2?: string | null
  email3?: string | null
  idLinkedOrganization?: number | null
  website?: string | null
}

/** Eine unzugeordnete Archiv-E-Mail (gruppiert je Mail, siehe inboxGrouping). */
export interface InboxGroupLike {
  primary: {
    id: number
    fromAddress?: string | null
    fromName?: string | null
    subject?: string | null
    bodyText?: string | null
    receivedAt?: string | null
    isRead?: boolean
    /** KARS-Adresse, an die die Kopie ging (Von/An-Filter). */
    toAddress?: string | null
    /** Echte An/Cc/Bcc-Empfänger der E-Mail (ohne Archivadresse), falls bekannt. */
    recipients?: Array<{ email: string, name?: string | null, role: string }> | null
  }
  items: Array<{ id: number, isRead?: boolean, filename?: string | null }>
}

/**
 * Ansicht und Filter der Kontaktliste. Posteingang/Gesendet/Papierkorb
 * (Kundenfeedback 02.10.2026, III): Posteingang zeigt nur empfangene,
 * Gesendet nur Nachrichten der eigenen Organisation; der Papierkorb gilt
 * nur für den angemeldeten Nutzer.
 */
export type ChatView = 'all' | 'unread' | 'inbox' | 'sent' | 'unassigned' | 'drafts' | 'outbox' | 'trash'

export interface ChatFilters {
  search: string
  view: ChatView
  date: string
  dateFrom: string
  dateTo: string
  sort: 'desc' | 'asc'
  /**
   * Von/An-Filter (Kundenfeedback 02.10.2026, II): E-Mail-Adressen in
   * Kleinschreibung. Beide zusammen gelten als UND; leer = kein Filter.
   */
  from: string[]
  to: string[]
}

export type CounterpartKind = 'contact' | 'organization' | 'email' | 'team' | 'group'
/** Anforderung 2.1: je Beteiligtem einzeln oder nach Gruppen gebündelt. */
export type GroupingMode = 'participant' | 'group'
export type Channel = 'email' | 'portal' | 'internal'

export interface CounterpartRef {
  key: string
  kind: CounterpartKind
  name: string
  email?: string
  /** Alle Adressen dieser Gegenseite in der Unterhaltung (mehrere Mitarbeiter). */
  addresses?: string[]
  /** Website aus den Stammdaten (Logo über die Firmen-Domain). */
  website?: string
  contactId?: number
  idOrganization?: number
}

export interface TeamMember {
  idUser: number
  name: string
  initials?: string | null
  image?: string | null
}

export interface Counterpart extends CounterpartRef {
  emails: string[]
  conversations: ChatConversation[]
  inbox: InboxGroupLike[]
  lastActivity: string
  lastMessage: ChatMessage | null
  /** Vorschau der letzten Aktivität (Nachricht oder Archiv-E-Mail). */
  preview: string
  previewOwn: boolean
  unreadCount: number
  caseNumbers: string[]
  /** Beteiligte der eigenen Seite (Avatar-Stapel in der Kontaktliste). */
  team: TeamMember[]
  channel: Channel
  /** Eine E-Mail an diese Gegenseite konnte nicht zugestellt werden. */
  deliveryProblem: boolean
  /** Fallfreie Unterhaltungen und Archiv-Mails, die einer Akte zuzuordnen sind. */
  unassigned: number
}

// ── Hilfen ─────────────────────────────────────────────────────────────

export function normalizeEmail (value?: string | null): string {
  return String(value || '').trim().toLowerCase()
}

export function stripHtml (html?: string | null): string {
  if (!html) {
    return ''
  }
  return String(html)
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
}

export function initialsOf (name?: string | null): string {
  const [first = '', second = ''] = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (first && second) {
    return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase()
  }
  if (first.length >= 2) {
    return first.slice(0, 2).toUpperCase()
  }
  return '?'
}

const AVATAR_TONES = 8

/** Stabile Farbe je Kontakt/Person (Index in die Avatar-Palette). */
export function avatarTone (seed: string): number {
  let hash = 0
  for (const ch of String(seed || '')) {
    hash = (hash * 31 + ch.charCodeAt(0)) >>> 0
  }
  return hash % AVATAR_TONES
}

function contactName (c: ContactLike): string {
  const person = `${(c.firstName || '').trim()} ${(c.lastName || '').trim()}`.trim()
  return (c.companyName || '').trim() || person || normalizeEmail(c.email)
}

export function timeOf (iso?: string | null): number {
  const t = new Date(iso || 0).getTime()
  return Number.isNaN(t) ? 0 : t
}

// ── Kontakte ───────────────────────────────────────────────────────────

export interface ContactIndex {
  byEmail: Map<string, ContactLike>
  byOrganization: Map<number, ContactLike>
  /** Firmen-Domain → Kontakt, nur wenn die Domain genau einem Kontakt gehört. */
  byDomain: Map<string, ContactLike>
}

/** Domains privater Postfächer: dort sagt die Domain nichts über die Firma. */
export const FREEMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch', 'web.de', 't-online.de',
  'outlook.com', 'outlook.de', 'hotmail.com', 'hotmail.de', 'live.com', 'live.de', 'msn.com',
  'yahoo.com', 'yahoo.de', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'aol.de', 'freenet.de',
  'mail.de', 'posteo.de', 'arcor.de', 'online.de', 'email.de', 'protonmail.com', 'proton.me', 'kabelmail.de',
])

export function emailDomain (email?: string | null): string {
  const addr = normalizeEmail(email)
  const at = addr.lastIndexOf('@')
  return at > 0 ? addr.slice(at + 1) : ''
}

export function buildContactIndex (contacts: ContactLike[]): ContactIndex {
  const byEmail = new Map<string, ContactLike>()
  const byOrganization = new Map<number, ContactLike>()
  const domainOwners = new Map<string, Set<number>>()
  const domainContact = new Map<string, ContactLike>()
  for (const c of contacts || []) {
    for (const addr of [c.email, c.email2, c.email3]) {
      const key = normalizeEmail(addr)
      if (!key) {
        continue
      }
      if (!byEmail.has(key)) {
        byEmail.set(key, c)
      }
      const domain = emailDomain(key)
      if (!domain || FREEMAIL_DOMAINS.has(domain)) {
        continue
      }
      domainOwners.set(domain, (domainOwners.get(domain) || new Set()).add(c.id))
      if (!domainContact.has(domain)) {
        domainContact.set(domain, c)
      }
    }
    if (c.idLinkedOrganization && !byOrganization.has(c.idLinkedOrganization)) {
      byOrganization.set(c.idLinkedOrganization, c)
    }
  }
  const byDomain = new Map<string, ContactLike>()
  for (const [domain, owners] of domainOwners) {
    const contact = domainContact.get(domain)
    if (owners.size === 1 && contact) {
      byDomain.set(domain, contact)
    }
  }
  return { byEmail, byOrganization, byDomain }
}

/** Kontakt zu einer Adresse: exakte Adresse, sonst eindeutige Firmen-Domain. */
function contactForEmail (index: ContactIndex, email: string): ContactLike | undefined {
  return index.byEmail.get(email) || index.byDomain.get(emailDomain(email))
}

function contactRef (c: ContactLike, email?: string): CounterpartRef {
  return {
    key: `contact:${c.id}`,
    kind: 'contact',
    name: contactName(c),
    contactId: c.id,
    ...(c.idLinkedOrganization ? { idOrganization: c.idLinkedOrganization } : {}),
    ...(c.website ? { website: c.website } : {}),
    ...(email ? { email } : {}),
  }
}

// ── Gegenseite einer Unterhaltung ──────────────────────────────────────

export function isOwnSideParticipant (p: ChatParticipant, viewer: Viewer): boolean {
  if (!p.idUser) {
    return false
  }
  // Ohne Organisationsangabe (ältere Backend-Version) gilt jeder interne
  // Nutzer als eigene Seite.
  if (!p.idOrganization) {
    return true
  }
  return p.idOrganization === viewer.idOrganization
}

export function refForParticipant (p: ChatParticipant, index: ContactIndex): CounterpartRef | null {
  const email = normalizeEmail(p.externalEmail || p.userEmail)
  if (p.idUser && p.idOrganization) {
    const linked = index.byOrganization.get(p.idOrganization)
    if (linked) {
      return contactRef(linked, email)
    }
    const byMail = email ? contactForEmail(index, email) : undefined
    if (byMail) {
      return contactRef(byMail, email)
    }
    return {
      key: `org:${p.idOrganization}`,
      kind: 'organization',
      name: (p.organizationName || '').trim() || participantDisplayName(p),
      idOrganization: p.idOrganization,
      ...(email ? { email } : {}),
    }
  }
  if (!email) {
    return null
  }
  const contact = contactForEmail(index, email)
  if (contact) {
    return contactRef(contact, email)
  }
  return { key: `email:${email}`, kind: 'email', name: (p.externalName || '').trim() || email, email }
}

/**
 * Alle Gegenseiten einer Unterhaltung (An und Cc). Eine Mail an das Autohaus
 * mit der Versicherung in Cc erscheint bei beiden – der Verlauf mit einem Kontakt
 * ist erst vollständig, wenn er auch die Mails enthält, in denen der Kontakt
 * nur in Kopie stand.
 */
export function counterpartsFor (conv: ChatConversation, viewer: Viewer, index: ContactIndex): CounterpartRef[] {
  const refs = new Map<string, CounterpartRef>()
  const others = (conv.participants || []).filter(p => !isOwnSideParticipant(p, viewer))
  // Bcc-Empfänger (z. B. die eigene Akte) bestimmen die Gegenseite nur, wenn
  // sonst niemand angeschrieben wurde.
  const visible = others.filter(p => (p.role || 'to').toLowerCase() !== 'bcc')
  for (const p of visible.length > 0 ? visible : others) {
    const ref = refForParticipant(p, index)
    if (!ref) {
      continue
    }
    const known = refs.get(ref.key)
    const addresses = [...(known?.addresses || []), ...(ref.email ? [ref.email] : [])]
    refs.set(ref.key, { ...(known || ref), email: known?.email || ref.email, addresses: [...new Set(addresses)] })
  }

  // Eingehende Mail eines Absenders, der (noch) kein Teilnehmer ist.
  if (refs.size === 0) {
    const inbound = conv.lastIncomingMessage
    const email = inbound?.sourceKind === 'email_in' ? normalizeEmail(inbound.senderEmail) : ''
    if (email) {
      const contact = contactForEmail(index, email)
      const ref = contact
        ? contactRef(contact, email)
        : { key: `email:${email}`, kind: 'email' as const, name: (inbound?.senderName || '').trim() || email, email }
      refs.set(ref.key, ref)
    }
  }
  if (refs.size > 0) {
    return [...refs.values()]
  }

  // Rein interne Unterhaltung: das Team ist die Gegenseite.
  const colleagues = (conv.participants || []).filter(p => p.idUser && p.idUser !== viewer.idUser)
  const ids = [...new Set(colleagues.map(p => Number(p.idUser)))].sort((a, b) => a - b)
  if (ids.length === 0) {
    return [{ key: 'team:self', kind: 'team', name: 'Interne Notizen' }]
  }
  const names = colleagues
    .filter((p, i, all) => all.findIndex(q => q.idUser === p.idUser) === i)
    .map(p => participantDisplayName(p))
  return [{ key: `team:${ids.join('-')}`, kind: 'team', name: names.join(', ') }]
}

export function inboxCounterpart (group: InboxGroupLike, index: ContactIndex): CounterpartRef {
  const email = normalizeEmail(group.primary.fromAddress)
  const contact = email ? contactForEmail(index, email) : undefined
  if (contact) {
    return contactRef(contact, email)
  }
  if (!email) {
    return { key: 'email:unbekannt', kind: 'email', name: 'Unbekannter Absender' }
  }
  return { key: `email:${email}`, kind: 'email', name: (group.primary.fromName || '').trim() || email, email }
}

// ── Kontaktliste ───────────────────────────────────────────────────────

export function conversationActivity (c: ChatConversation): string {
  return c.lastMessage?.createdAt || c.updatedAt || c.createdAt || ''
}

function conversationChannel (c: ChatConversation, viewer: Viewer): Channel {
  const parts = c.participants || []
  if (parts.some(p => p.externalEmail && !isOwnSideParticipant(p, viewer))) {
    return 'email'
  }
  const kind = c.lastMessage?.sourceKind || ''
  if (kind === 'email_in' || kind === 'email_out') {
    return 'email'
  }
  if (parts.some(p => p.idUser && !isOwnSideParticipant(p, viewer))) {
    return 'portal'
  }
  return 'internal'
}

interface Bucket {
  ref: CounterpartRef
  emails: Set<string>
  conversations: ChatConversation[]
  inbox: InboxGroupLike[]
}

/**
 * Gebündelte Gruppierung: Eine Unterhaltung mit mehreren Gegenseiten bildet
 * eine eigene Gruppe (wie ein Gruppenchat), statt bei jeder Gegenseite
 * einzeln zu erscheinen.
 */
/**
 * Steht die Unterhaltung unter „Zuordnen"? Nur fallfreie E-Mail-
 * Unterhaltungen, die nicht als „Nicht zuordnen" markiert sind
 * (Kundenfeedback 02.10.2026, erster Teil, IV).
 */
export function needsCaseAssignment (c: ChatConversation, viewer: Viewer): boolean {
  return !c.idCase && !c.noCase && conversationChannel(c, viewer) === 'email'
}

function groupRef (refs: CounterpartRef[]): CounterpartRef {
  if (refs.length < 2) {
    return refs[0]!
  }
  const keys = refs.map(r => r.key).sort()
  return {
    key: `group:${keys.join('|')}`,
    kind: 'group',
    name: refs.map(r => r.name).join(', '),
    addresses: [...new Set(refs.flatMap(r => r.addresses || (r.email ? [r.email] : [])))],
  }
}

export function groupByCounterpart (
  conversations: ChatConversation[],
  viewer: Viewer,
  index: ContactIndex,
  inbox: InboxGroupLike[] = [],
  mode: GroupingMode = 'participant',
): Counterpart[] {
  const buckets = new Map<string, Bucket>()
  const bucketFor = (ref: CounterpartRef): Bucket => {
    let b = buckets.get(ref.key)
    if (!b) {
      b = { ref, emails: new Set(), conversations: [], inbox: [] }
      buckets.set(ref.key, b)
    }
    for (const email of ref.addresses || (ref.email ? [ref.email] : [])) {
      b.emails.add(email)
    }
    return b
  }

  const sorted = [...conversations].sort((a, b) => timeOf(conversationActivity(b)) - timeOf(conversationActivity(a)))
  for (const c of sorted) {
    const refs = counterpartsFor(c, viewer, index)
    for (const ref of mode === 'group' ? [groupRef(refs)] : refs) {
      bucketFor(ref).conversations.push(c)
    }
  }
  for (const g of inbox) {
    bucketFor(inboxCounterpart(g, index)).inbox.push(g)
  }

  return [...buckets.values()]
    .map(b => toCounterpart(b, viewer))
    .sort((a, b) => timeOf(b.lastActivity) - timeOf(a.lastActivity))
}

function toCounterpart (b: Bucket, viewer: Viewer): Counterpart {
  const latestConv = b.conversations[0] || null
  const latestInbox = [...b.inbox].sort((x, y) => timeOf(y.primary.receivedAt) - timeOf(x.primary.receivedAt))[0] || null
  const convTime = latestConv ? conversationActivity(latestConv) : ''
  const inboxTime = latestInbox?.primary.receivedAt || ''
  const inboxIsLatest = Boolean(latestInbox) && timeOf(inboxTime) > timeOf(convTime)

  const lastMessage = latestConv?.lastMessage || null
  const preview = inboxIsLatest
    ? latestInbox!.primary.subject || stripHtml(latestInbox!.primary.bodyText) || ''
    : stripHtml(lastMessage?.content) || latestConv?.subject || ''

  const team = new Map<number, TeamMember>()
  for (const c of b.conversations) {
    for (const p of c.participants || []) {
      if (!p.idUser || !isOwnSideParticipant(p, viewer) || team.has(p.idUser)) {
        continue
      }
      team.set(p.idUser, { idUser: p.idUser, name: participantDisplayName(p), initials: p.initials, image: p.image })
    }
  }

  const caseNumbers = [...new Set(b.conversations.map(c => (c.caseNumber || '').trim()).filter(Boolean))]
  const channels = new Set(b.conversations.map(c => conversationChannel(c, viewer)))
  if (b.inbox.length > 0) {
    channels.add('email')
  }

  return {
    ...b.ref,
    emails: [...b.emails],
    conversations: b.conversations,
    inbox: b.inbox,
    lastActivity: inboxIsLatest ? inboxTime : convTime,
    lastMessage,
    preview,
    previewOwn: !inboxIsLatest && Boolean(lastMessage && isOwnMessage(lastMessage, viewer)),
    unreadCount: b.conversations.reduce((s, c) => s + Number(c.unreadCount || 0), 0)
      + b.inbox.filter(g => g.items.some(i => !i.isRead)).length,
    caseNumbers,
    team: [...team.values()],
    channel: channels.has('email') ? 'email' : (channels.has('portal') ? 'portal' : 'internal'),
    deliveryProblem: b.conversations.some(c => c.deliveryProblem),
    unassigned: b.inbox.length + b.conversations.filter(c => needsCaseAssignment(c, viewer)).length,
  }
}

export function matchesCounterpart (cp: Counterpart, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) {
    return true
  }
  const haystack = [
    cp.name,
    ...cp.emails,
    ...cp.caseNumbers,
    cp.preview,
    ...cp.conversations.map(c => c.subject || ''),
    ...cp.conversations.map(c => c.caseDescription || ''),
    ...cp.inbox.map(g => g.primary.subject || ''),
    ...cp.team.map(t => t.name),
  ]
  return haystack.some(v => String(v || '').toLowerCase().includes(q))
}

// ── Nachrichten ────────────────────────────────────────────────────────

/** Stammt die Nachricht von der eigenen Organisation (rechts im Verlauf)? */
export function isOwnMessage (m: ChatMessage, viewer: Viewer): boolean {
  if (m.sourceKind === 'email_in' || !m.idUser) {
    return false
  }
  if (m.senderIdOrganization) {
    return m.senderIdOrganization === viewer.idOrganization
  }
  return m.idUser === viewer.idUser
}

export function senderName (m: ChatMessage): string {
  return (m.userName || m.senderName || m.senderEmail || 'Unbekannt').trim()
}

// ── Posteingang / Gesendet / Papierkorb ────────────────────────────────

/** Ist die Nachricht eine dokumentierte E-Mail (Richtung korrigierbar)? */
export function isEmailMessage (m: Pick<ChatMessage, 'sourceKind'>): boolean {
  return m.sourceKind === 'email_in' || m.sourceKind === 'email_out'
}

/** Absender der automatischen Unzustellbarkeits-Hinweise (Backend: delivery.NDRSenderEmail). */
export const DELIVERY_SERVICE_EMAIL = 'zustelldienst@karsconnection.de'

/**
 * Enthält die Unterhaltung E-Mails, deren Absender/Richtung sich festlegen
 * lässt? Grundlage sind die Listenfelder (jüngste Nachricht je Richtung).
 */
export function hasClassifiableEmail (c: Pick<ChatConversation, 'lastMessage' | 'lastIncomingMessage' | 'lastOutgoingMessage'>): boolean {
  return [c.lastMessage, c.lastIncomingMessage, c.lastOutgoingMessage].some(m => Boolean(m) && canChangeDirection(m!))
}

/** Hinweise des Zustelldienstes sind keine Korrespondenz – Richtung nicht änderbar. */
export function canChangeDirection (m: Pick<ChatMessage, 'sourceKind' | 'senderEmail'>): boolean {
  return isEmailMessage(m) && normalizeEmail(m.senderEmail) !== DELIVERY_SERVICE_EMAIL
}

/**
 * Gehört die Nachricht in die Ansicht? Posteingang = nur empfangene
 * Nachrichten (Gegenseite), Gesendet = nur Nachrichten der eigenen
 * Organisation; alle anderen Ansichten zeigen den ganzen Verlauf.
 */
export function matchesDirectionView (m: ChatMessage, view: ChatView, viewer: Viewer): boolean {
  if (view === 'inbox') {
    return !isOwnMessage(m, viewer)
  }
  if (view === 'sent') {
    return isOwnMessage(m, viewer)
  }
  return true
}

/**
 * Hat der Kontakt empfangene Nachrichten? Die Liste kennt nur die jüngste
 * Nachricht je Richtung (aus Sicht des Nutzers) – das ist eine Näherung;
 * der Verlauf selbst filtert je Nachricht (matchesDirectionView).
 */
export function hasIncomingMessages (cp: Pick<Counterpart, 'conversations' | 'inbox'>, viewer: Viewer): boolean {
  return cp.inbox.length > 0 || cp.conversations.some(c =>
    (c.lastIncomingMessage && !isOwnMessage(c.lastIncomingMessage, viewer))
    || (c.lastMessage && !isOwnMessage(c.lastMessage, viewer)))
}

/** Hat die eigene Organisation diesem Kontakt etwas gesendet? */
export function hasOutgoingMessages (cp: Pick<Counterpart, 'conversations'>, viewer: Viewer): boolean {
  return cp.conversations.some(c =>
    Boolean(c.lastOutgoingMessage)
    || (c.lastMessage && isOwnMessage(c.lastMessage, viewer))
    || (c.lastIncomingMessage && isOwnMessage(c.lastIncomingMessage, viewer)))
}

/** Unterhaltungen nach Papierkorb trennen: [aktiv, im Papierkorb]. */
/**
 * Zentrales Postfach (Kundenfeedback 21.10.2026, I): Einer Akte zugeordnete
 * Unterhaltungen verschwinden aus der globalen Liste, solange „Zugeordnete
 * ausblenden" aktiv ist. Im Fall (scope="case") gilt das nie.
 */
export function withoutAssigned (conversations: ChatConversation[], hide: boolean): ChatConversation[] {
  return hide ? conversations.filter(c => !c.idCase) : conversations
}

export function splitTrashed (conversations: ChatConversation[]): [ChatConversation[], ChatConversation[]] {
  return [conversations.filter(c => !c.trashed), conversations.filter(c => Boolean(c.trashed))]
}

/** Erfüllt der Kontakt die Richtungs-/Papierkorb-Ansicht? */
export function matchesCounterpartView (cp: Counterpart, view: ChatView, viewer: Viewer): boolean {
  if (view === 'inbox') {
    return hasIncomingMessages(cp, viewer)
  }
  if (view === 'sent') {
    return hasOutgoingMessages(cp, viewer)
  }
  if (view === 'trash') {
    return cp.conversations.some(c => Boolean(c.trashed))
  }
  return true
}

export interface Recipients {
  to: string[]
  cc: string[]
  bcc: string[]
}

/**
 * Empfänger einer Nachricht aus den Rollen der Unterhaltung (An / Cc / Bcc),
 * ohne den Absender selbst. Bcc ist nur an eigenen Nachrichten sichtbar –
 * die Gegenseite hat diese Empfänger nie gesehen.
 */
export function recipientsOf (m: ChatMessage, conv: ChatConversation | null | undefined, viewer: Viewer): Recipients {
  const out: Recipients = { to: [], cc: [], bcc: [] }
  const senderEmail = normalizeEmail(m.senderEmail)
  const own = isOwnMessage(m, viewer)
  if (m.recipients?.length) {
    return actualRecipients(m.recipients, conv, own)
  }
  for (const p of conv?.participants || []) {
    if (m.idUser && p.idUser === m.idUser) {
      continue
    }
    if (!m.idUser && senderEmail && normalizeEmail(p.externalEmail) === senderEmail) {
      continue
    }
    const name = participantDisplayName(p)
    if (!name) {
      continue
    }
    const role = (p.role || 'to').toLowerCase()
    if (role === 'bcc') {
      if (own) {
        out.bcc.push(name)
      }
    } else if (role === 'cc') {
      out.cc.push(name)
    } else {
      out.to.push(name)
    }
  }
  return out
}

/** Empfänger aus den Zustellungen; Namen wie in der Teilnehmerliste, sonst Adresse. */
function actualRecipients (
  list: NonNullable<ChatMessage['recipients']>,
  conv: ChatConversation | null | undefined,
  own: boolean,
): Recipients {
  const out: Recipients = { to: [], cc: [], bcc: [] }
  const byEmail = new Map((conv?.participants || [])
    .map(p => [normalizeEmail(p.externalEmail || p.userEmail), participantDisplayName(p)] as const)
    .filter(([email, name]) => email && name))
  for (const r of list) {
    const email = normalizeEmail(r.email)
    const name = byEmail.get(email) || (r.name || '').trim() || email
    const role = (r.role || 'to').toLowerCase()
    if (role === 'bcc') {
      if (own) {
        out.bcc.push(name)
      }
    } else if (role === 'cc') {
      out.cc.push(name)
    } else {
      out.to.push(name)
    }
  }
  return out
}

// ── Filter der Kontaktliste ────────────────────────────────────────────

const DAY_MS = 86_400_000

/** Liegt der Zeitpunkt im gewählten Zeitraum (Heute, n Tage, eigener Zeitraum)? */
export function matchesDateFilter (iso: string, f: Pick<ChatFilters, 'date' | 'dateFrom' | 'dateTo'>, now: Date = new Date()): boolean {
  if (f.date === 'all') {
    return true
  }
  const d = new Date(iso || '')
  if (Number.isNaN(d.getTime())) {
    return false
  }
  if (f.date === 'custom') {
    if (f.dateFrom && d < new Date(`${f.dateFrom}T00:00:00`)) {
      return false
    }
    if (f.dateTo && d > new Date(`${f.dateTo}T23:59:59`)) {
      return false
    }
    return true
  }
  if (f.date === 'today') {
    return d.toDateString() === now.toDateString()
  }
  const days = Number(f.date)
  return !Number.isFinite(days) || now.getTime() - d.getTime() <= days * DAY_MS
}

/** Kontaktliste nach Ansicht, Suche, Zeitraum und Sortierung. */
export function filterCounterparts (
  list: Counterpart[],
  f: ChatFilters,
  now: Date = new Date(),
  peopleMatches: ReadonlySet<number> | null = null,
): Counterpart[] {
  const out = list.filter(cp => (f.view !== 'unread' || cp.unreadCount > 0)
    && (f.view !== 'unassigned' || cp.unassigned > 0)
    && matchesCounterpart(cp, f.search)
    && matchesDateFilter(cp.lastActivity, f, now)
    && counterpartMatchesPeople(cp, f, peopleMatches))
  const dir = f.sort === 'asc' ? 1 : -1
  return [...out].sort((a, b) => dir * (timeOf(a.lastActivity) - timeOf(b.lastActivity)))
}

// ── Von/An-Filter (Kundenfeedback 02.10.2026, II) ──────────────────────

export type PeopleFilter = Pick<ChatFilters, 'from' | 'to'>

export function hasPeopleFilter (f: PeopleFilter): boolean {
  return f.from.length > 0 || f.to.length > 0
}

/** Freie Eingaben der Von/An-Felder: trim, Kleinschreibung, nur Adressen, ohne Dubletten. */
export function normalizePeopleList (values: unknown[] | null | undefined): string[] {
  const out: string[] = []
  for (const v of values || []) {
    const email = normalizeEmail(typeof v === 'string' ? v : (v as { value?: string } | null)?.value)
    if (email.includes('@') && !out.includes(email)) {
      out.push(email)
    }
  }
  return out
}

/** Absenderadresse einer Nachricht: eigene Nutzer über die Teilnehmerliste, Externe über senderEmail. */
export function messageSenderEmail (m: ChatMessage, conv: ChatConversation | null | undefined): string {
  const direct = normalizeEmail(m.senderEmail)
  if (direct) {
    return direct
  }
  if (!m.idUser) {
    return ''
  }
  const p = (conv?.participants || []).find(x => x.idUser === m.idUser)
  return normalizeEmail(p?.userEmail || p?.externalEmail)
}

/** Empfängeradressen einer Nachricht: Zustellungen/Kopfzeilen, sonst die übrigen Teilnehmer. */
export function messageRecipientEmails (m: ChatMessage, conv: ChatConversation | null | undefined): string[] {
  if (m.recipients?.length) {
    return [...new Set(m.recipients.map(r => normalizeEmail(r.email)).filter(Boolean))]
  }
  const sender = messageSenderEmail(m, conv)
  const out = new Set<string>()
  for (const p of conv?.participants || []) {
    if (m.idUser && p.idUser === m.idUser) {
      continue
    }
    const email = normalizeEmail(p.externalEmail || p.userEmail)
    if (email && email !== sender) {
      out.add(email)
    }
  }
  return [...out]
}

/**
 * Trifft der Von/An-Filter auf diese Nachricht zu? Von = Absender, An =
 * mindestens ein Empfänger (An/Cc/Bcc); beide zusammen als UND.
 */
export function matchesPeopleFilter (m: ChatMessage, conv: ChatConversation | null | undefined, f: PeopleFilter): boolean {
  if (f.from.length > 0 && !f.from.includes(messageSenderEmail(m, conv))) {
    return false
  }
  if (f.to.length > 0) {
    const recipients = messageRecipientEmails(m, conv)
    if (!recipients.some(r => f.to.includes(r))) {
      return false
    }
  }
  return true
}

/** Archiv-E-Mail (noch keiner Akte zugeordnet) gegen den Von/An-Filter prüfen. */
export function inboxGroupMatchesPeople (g: InboxGroupLike, f: PeopleFilter): boolean {
  if (f.from.length > 0 && !f.from.includes(normalizeEmail(g.primary.fromAddress))) {
    return false
  }
  if (f.to.length > 0) {
    const recipients = (g.primary.recipients || []).map(r => normalizeEmail(r.email))
    if (recipients.length === 0 && g.primary.toAddress) {
      recipients.push(normalizeEmail(g.primary.toAddress))
    }
    if (!recipients.some(r => f.to.includes(r))) {
      return false
    }
  }
  return true
}

/**
 * Kontakt nur zeigen, wenn eine seiner Unterhaltungen in der serverseitigen
 * Treffermenge liegt (peopleMatches) oder eine seiner Archiv-Mails passt.
 * Ohne Von/An-Filter immer; mit Filter, aber noch ohne Treffermenge, ebenfalls
 * (die Liste wird nach dem Laden eingeschränkt).
 */
export function counterpartMatchesPeople (cp: Counterpart, f: PeopleFilter, peopleMatches: ReadonlySet<number> | null): boolean {
  if (!hasPeopleFilter(f)) {
    return true
  }
  if (cp.inbox.some(g => inboxGroupMatchesPeople(g, f))) {
    return true
  }
  if (peopleMatches === null) {
    return cp.conversations.length > 0
  }
  return cp.conversations.some(c => peopleMatches.has(c.id))
}

// ── Seitenweises Laden ─────────────────────────────────────────────────

/** Unterhaltungen zusammenführen: neue ergänzen, vorhandene aktualisieren. */
export function mergeConversations (existing: ChatConversation[], incoming: ChatConversation[]): ChatConversation[] {
  const byId = new Map(existing.map(c => [c.id, c]))
  for (const c of incoming) {
    byId.set(c.id, c)
  }
  return [...byId.values()]
}

/**
 * Filter für die Serversuche nach allen Unterhaltungen eines Kontakts:
 * seine Adressen und die fremden Organisationen seiner Portal-Nutzer.
 */
export function counterpartFilter (cp: Counterpart, viewer: Viewer): { emails: string[], orgIds: number[] } {
  const orgIds = new Set<number>()
  if (cp.idOrganization) {
    orgIds.add(cp.idOrganization)
  }
  const emails = new Set(cp.emails.map(e => normalizeEmail(e)).filter(Boolean))
  for (const c of cp.conversations) {
    for (const p of c.participants || []) {
      if (p.idUser && p.idOrganization && p.idOrganization !== viewer.idOrganization) {
        orgIds.add(p.idOrganization)
      }
    }
  }
  return { emails: [...emails], orgIds: [...orgIds] }
}
